import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { paths } from 'paths';
import { useAppSelector } from 'store';
import type { ContextChildren, Nullable, Optional, ZendeskConversationField, ZendeskCookieRange } from 'types';
import { createUseContext } from 'utils/context';
import { isTokenExpired } from 'utils/jwt';
import { PersistService } from 'utils/persist';

import { initialize } from './helpers';

type TZendeskContext = {
  show: () => void;
  hide: () => void;
  open: () => void;
  close: () => void;

  setLocale: (newLocale: string) => void;
  setZIndex: (newZIndex: number) => void;
  setCookies: (range: ZendeskCookieRange) => void;

  setConversationFields: (conversationFields: ZendeskConversationField[]) => void;
  setConversationTags: (conversationTags: string[]) => void;

  loginUser: (accessToken: string) => Promise<void>;
  logoutUser: () => void;

  isOpen: boolean;
  isLoaded: boolean;

  unreadMessages: Nullable<number>;
};

const ZendeskContext = createContext<Optional<TZendeskContext>>(undefined);

type ZendeskProviderProps = {
  children: ContextChildren<TZendeskContext>;
};

async function fetchJwtToken(accessToken: string): Promise<Nullable<string>> {
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/account/zendesk-jwt`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const { token } = await response.json();

    return token as string;
  } catch {
    return null;
  }
}

export function ZendeskProvider({ children }: ZendeskProviderProps) {
  const { pathname } = useLocation();
  const { accessToken, isAuthenticated } = useAppSelector((state) => state.auth);
  const isLoaded = useRef(false);

  const [isOpen, setOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState<Nullable<number>>(null);

  const shouldBeVisible = useMemo(() => {
    return ![paths.order.checkout, paths.order.details, paths.order.index].includes(pathname);
  }, [pathname]);

  const register = useCallback(() => {
    if (isLoaded.current) return;

    window.zE?.('messenger:on', 'open', () => setOpen(true));

    window.zE?.('messenger:on', 'close', () => setOpen(false));

    window.zE?.('messenger:on', 'unreadMessages', (messages) => setUnreadMessages(messages));

    window.zE?.('messenger:set', 'zIndex', shouldBeVisible ? 999999 : -1);

    isLoaded.current = true;
  }, [shouldBeVisible]);

  useEffect(() => {
    initialize('d13e103f-27f6-40b8-a586-70f1edfd4b47', register);
  }, [register]);

  const show = useCallback(() => window.zE?.('messenger', 'show'), []);

  const hide = useCallback(() => window.zE?.('messenger', 'hide'), []);

  const open = useCallback(() => {
    if (!shouldBeVisible) window.zE?.('messenger:set', 'zIndex', 999999);

    window.zE?.('messenger', 'open');
  }, [shouldBeVisible]);

  const close = useCallback(() => {
    window.zE?.('messenger', 'close');

    if (!shouldBeVisible) window.zE?.('messenger:set', 'zIndex', -1);
  }, [shouldBeVisible]);

  const setLocale = useCallback((locale: string) => window.zE?.('messenger:set', 'locale', locale), []);

  const setZIndex = useCallback((zIndex: number) => window.zE?.('messenger:set', 'zIndex', zIndex), []);

  const setCookies = useCallback((range: ZendeskCookieRange) => window.zE?.('messenger:set', 'cookies', range), []);

  const setConversationFields = useCallback(
    (conversationFields: ZendeskConversationField[]) =>
      window.zE?.('messenger:set', 'conversationFields', conversationFields),
    [],
  );

  const setConversationTags = useCallback(
    (conversationTags: string[]) => window.zE?.('messenger:set', 'conversationTags', conversationTags),
    [],
  );

  const getJwtToken = useCallback(async (token: string) => {
    const prevJwt = PersistService.get<string>('auth.zendesk.jwt');

    if (!prevJwt || isTokenExpired(prevJwt)) {
      const newToken = await fetchJwtToken(token);

      PersistService.set('auth.zendesk.jwt', newToken);

      return newToken;
    }

    return prevJwt;
  }, []);

  const loginUser = useCallback(
    async (token: Nullable<string>) => {
      if (!token) return;

      window.zE?.(
        'messenger',
        'loginUser',
        async (callback) => {
          try {
            const jwt = await getJwtToken(token);

            if (!jwt) return;

            callback(jwt);
          } catch {
            // noop
          }
        },
        () => {
          // Noop
        },
      );
    },
    [getJwtToken],
  );

  const logoutUser = useCallback(() => window.zE?.('messenger', 'logoutUser'), []);

  useEffect(() => {
    if (!isOpen) return;

    if (!isAuthenticated || !accessToken) return;

    loginUser(accessToken);
  }, [accessToken, isAuthenticated, isOpen, loginUser]);

  const value = useMemo<TZendeskContext>(() => {
    return {
      close,
      hide,
      isLoaded: isLoaded.current,
      isOpen,
      open,
      setConversationFields,
      setConversationTags,
      setCookies,
      setLocale,
      setZIndex,
      loginUser,
      logoutUser,
      show,
      unreadMessages,
    };
  }, [
    close,
    hide,
    isOpen,
    loginUser,
    logoutUser,
    open,
    setConversationFields,
    setConversationTags,
    setCookies,
    setLocale,
    setZIndex,
    show,
    unreadMessages,
  ]);

  return (
    <ZendeskContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </ZendeskContext.Provider>
  );
}

export const useZendesk = createUseContext(ZendeskContext);
